<template>
  <div id="pcb-form">
    <Spin size="large" fix v-if="loading"></Spin>
    <Form ref="pcbSpecForm" class="pcb-spec-form" :model="pcbSpec" label-position="top" :rules="ruleValidate">
      <Row :gutter="32" class="align-items-center">
        <Col span="12">
          <Row class="align-items-end">
            <Col span="8">
              <FormItem prop="width" label="Dimension" required>
                <InputNumber
                  :min="0"
                  :precision="3"
                  v-model.number="pcbSpec.width"
                  placeholder="Width"
                  :active-change="false"
                  required
                ></InputNumber>
              </FormItem>
            </Col>
            <Col span="2" class="pcb-spec-form-suffix">X</Col>
            <Col span="8">
              <FormItem prop="height" required>
                <InputNumber
                  :min="0"
                  :precision="3"
                  v-model.number="pcbSpec.height"
                  placeholder="Height"
                  :active-change="false"
                ></InputNumber>
              </FormItem>
            </Col>
            <Col span="6" class="pcb-spec-form-suffix">{{ pcbSpec.measurement_unit }} /piece</Col>
          </Row>
        </Col>
        <Col span="12">
          <FormItem prop="measurementUnit" label="Measurement unit">
            <Select v-model="pcbSpec.measurement_unit">
              <Option
                v-for="item in selectOptions.measurementUnitList"
                :value="item.value"
                :key="item.value"
              >{{ item.label }}</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem prop="layers" label="Layers" required>
            <InputNumber
                  :min="0"
                  v-model.number="pcbSpec.layers"
                  placeholder="Layers"
                  :active-change="false"
                ></InputNumber>
          </FormItem>
        </Col>
        <Col span="12">
          <Row class="align-items-end">
            <Col span="10">
              <FormItem prop="different_designs" label="Different designs">
                <Input
                  :min="0"
                  v-model.number="pcbSpec.different_designs"
                  placeholder="Different designs"
                  :active-change="false"
                  type="number"
                ></Input>
              </FormItem>
            </Col>
            <Col span="14" class="pcb-spec-form-suffix">(Number of PCB boards in Gerber)</Col>
          </Row>
        </Col>
        <Col span="12">
          <FormItem prop="material_details" label="Materials" required>
            <AutoComplete
              v-model="pcbSpec.material_details"
              :data="autoSearchData.material_details"
              clearable>
            </AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem prop="pcb_standard" label="PCB standard">
            <AutoComplete
              v-model="pcbSpec.pcb_standard"
              :data="autoSearchData.pcb_standard"
              clearable>
            </AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <Row class="align-items-end">
            <Col span="18">
              <FormItem prop="thickness" label="Finished thickness" required>
                <InputNumber
                  :min="0"
                  :precision="2"
                  v-model.number="pcbSpec.thickness"
                  placeholder="Finished Thickness"
                  :active-change="false"
                ></InputNumber>
              </FormItem>
            </Col>
            <Col span="6" class="pcb-spec-form-suffix">{{ pcbSpec.measurement_unit }}</Col>
          </Row>
        </Col>
        <Col span="12">
          <FormItem prop="solder_mask" label="Solder mask">
            <AutoComplete
              v-model="pcbSpec.solder_mask"
              :data="autoSearchData.solder_mask"
              clearable>
            </AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem prop="legendColor" label="Legend color">
            <AutoComplete
              v-model="pcbSpec.legend_color"
              :data="autoSearchData.legend_color"
              clearable>
            </AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem prop="surfaceFinish" label="Surface finish">
            <AutoComplete
              v-model="pcbSpec.surface_finish"
              :data="autoSearchData.surface_finish"
              clearable>
            </AutoComplete>
          </FormItem>
        </Col>
        <Col span="12">
          <FormItem prop="minTraceSpace" label="Min. trace/space(mil)">
            <InputNumber
              :min="0"
              :precision="2"
              v-model.number="pcbSpec.min_trace_space"
              placeholder="Min. Trace/Space"
              :active-change="false"
            ></InputNumber>
          </FormItem>
        </Col>
        <Col span="12">
          <Row class="align-items-end">
            <Col span="18">
              <FormItem prop="minHoleSizes" label="Min. hole sizes">
                <InputNumber
                  :min="0"
                  :precision="2"
                  v-model.number="pcbSpec.min_hole_sizes"
                  placeholder="Min. Hole Sizes"
                  :active-change="false"
                ></InputNumber>
              </FormItem>
            </Col>
            <Col span="6" class="pcb-spec-form-suffix">{{ pcbSpec.measurement_unit }}</Col>
          </Row>
        </Col>
        <Col span="12">
          <FormItem prop="impedance_test" :label="$t('pcb.form.impedance_test.field_name')">
            <Select v-model="pcbSpec.impedance_test">
              <Option :value="0" :key="false">{{ $t('pcb.form.impedance_test.no') }}</Option>
              <Option :value="1" :key="true">{{ $t('pcb.form.impedance_test.yes') }}</Option>
            </Select>
          </FormItem>
        </Col>
        <Col span="12">
          <Row class="align-items-end">
            <Col span="24">
              <FormItem prop="copperWeight" label="Copper weight">
                <CopperWeightField v-if="pcbSpec && loaded" v-model="pcbSpec.copper_weights" required></CopperWeightField>
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>
      <div class="actions text-align-right">
        <Button type="primary" size="large" @click="savePcbSpecs" :loading="saving">{{ $t('pcb.button.save_pcb_spec') }}</Button>
      </div>
    </Form>
  </div>
</template>

<script>
import length from 'length.js'
import { mapActions } from 'vuex'
import SelectOptions from '../config/select-options.js'
import CopperWeightField from './copper-weight-field'
import PcbSpecValidations from './pcb-spec-validations'

export default {
  name: 'PcbSpecForm',
  components: {
    CopperWeightField
  },
  data() {
    return {
      pcbId: null,
      measurementUnit: 'mm',
      selectOptions: SelectOptions,
      saving: false,
      loading: false,
      loaded: false,
      currentPcb: {},
      pcb: {
        name: '',
        description: ''
      },
      pcbSpec: {
        width: null,
        height: null,
        layers: null,
        material_details: null,
        thickness: 0,
        solder_mask: null,
        legend_color: null,
        surface_finish: null,
        copper_weights: [],
        min_hole_sizes: 0,
        measurement_unit: 'mm',
        min_trace_space: 0,
        impedance_test: 0,
        date_code: null,
        pcb_standard: 'IPC 600G Class II',
        final_finish: '',
        different_designs: 1
      },
      ruleValidate: PcbSpecValidations,
      autoSearchData: {}

    }
  },
  watch: {
    currentPcb: {
      deep: true,
      handler () {
        if (this.currentPcb) {
          this.pcb = _.cloneDeep(this.currentPcb)
          this.pcbSpec = Object.assign({}, this.pcbSpec, this.currentPcb.specification)
        }
      }
    },
    'pcbSpec.measurement_unit': function(val) {
      // CHANGING UNIT VALUE WITH CHANGE IN MESAUREMENT UNITS
      const lengthUnit = val === 'inches' ? 'in' : val
      const originUnit = this.pcbSpec.measurement_unit === 'inches' ? 'in' : this.pcbSpec.measurement_unit

      const width = this.pcbSpec.width ? this.pcbSpec.width : 0
      const height = this.pcbSpec.height ? this.pcbSpec.height : 0

      const originWidth = length(width, originUnit)
      const originHeight = length(height, originUnit)

      this.pcbSpec.width = this._.round(originWidth.to(lengthUnit).value, 4)
      this.pcbSpec.height = this._.round(originHeight.to(lengthUnit).value, 4)

      const originthickness = length(this.pcbSpec.thickness, originUnit)
      const originminHoleSizes = length(this.pcbSpec.minHoleSizes, originUnit)

      this.pcbSpec.thickness = originthickness.to(lengthUnit).value
      this.pcbSpec.min_hole_sizes = originminHoleSizes.to(lengthUnit).value
      this.pcbSpec.measurement_unit = val
    }
  },
  mounted() {
    this.pcbId = this.$route.params.partId
    if (this.pcbId) {
      this.loadPcbSpecs(this.pcbId)
    }
    this.loadSpecValues()
  },
  methods: {
    ...mapActions([
      'loadPcbSpecValues',
      'savePcbSpecValues',
      'searchPcbSpecifications'
    ]),
    loadPcbSpecs (id) {
      this.loading = true
      this.loadPcbSpecValues(id).then(resp => {
        if (!_.isEmpty(resp.data)) {
          this.pcbSpec = resp.data
        }
        this.loaded = true
      }).finally(() => { this.loading = false })
    },
    savePcbSpecs () {
      this.$refs['pcbSpecForm'].validate((valid) => {
        if (valid) {
          this.saving = true
          this.savePcbSpecValues({ id: this.pcbId, data: this.pcbSpec }).then(resp => {
            this.$Message.success(this.$t('save_success_message'))
          }).finally(() => { this.saving = false })
        } else {
          this.$Message.error('Please provide values for the required fields')
        }
      })
    },
    loadSpecValues () {
      this.searchPcbSpecifications(this.getSpecificationsSearchFilters()).then(res => {
        let resultData = res.aggs
        for (const [key, aggData] of Object.entries(resultData)) {
          this.autoSearchData[key] = this.getSpecificationBucketValues(aggData)
        }
      })
    },
    getSpecificationsSearchFilters () {
      return {
        keyword: '',
        matches: {},
        terms: {},
        specs: {},
        exists: {},
        includes: [],
        aggs: {
          copper_weights: {
            terms: {
              field: "copper_weights.value.keyword",
              size: 100
            }
          },
          layers: {
            terms: {
              field: "layers",
              size: 100
            }
          },
          min_hole_sizes: {
            terms: {
              field: "min_hole_sizes",
              size: 100
            }
          },
          final_finish: {
            terms: {
              field: "final_finish",
              size: 100
            }
          },
          different_desgins: {
            terms: {
              field: "different_desgins",
              size: 100
            }
          },
          height: {
            terms: {
              field: "height",
              size: 100
            }
          },
          width: {
            terms: {
              field: "width",
              size: 100
            }
          },
          pcb_standard: {
            terms: {
              field: "pcb_standard",
              size: 100
            }
          },
          material_details: {
            terms: {
              field: "material_details",
              size: 100
            }
          },
          thickness: {
            terms: {
              field: "thickness",
              size: 100
            }
          },
          solder_mask: {
            terms: {
              field: "solder_mask",
              size: 100
            }
          },
          legend_color: {
            terms: {
              field: "legend_color",
              size: 100
            }
          },
          surface_finish: {
            terms: {
              field: "surface_finish",
              size: 100
            }
          },
          measurement_unit: {
            terms: {
              field: "measurement_unit",
              size: 100
            }
          },
          min_trace_space: {
            terms: {
              field: "min_trace_space",
              size: 100
            }
          },
          version: {
            terms: {
              field: "version",
              size: 100
            }
          }
        },
        size: 0
      }
    },
    getSpecificationBucketValues(aggData) {
      return aggData.buckets ? aggData.buckets.map((val) => {
        return val.key
      }) : []
    }

  }
}
</script>

<style lang="less">
.pcb-spec-form {

  .pcb-spec-form-labels {
    margin-top: 10px;
    text-align: right;
    line-height: 15px;
    padding-right: 10px;
    font-weight: normal;
    // font-size: 14px;
  }
  .pcb-spec-form-suffix {
    text-align: left;
    margin: 10px 0 32px;
    line-height: 15px;
    padding-left: 10px;
    font-size: 12px;
  }
}
</style>
